import { useEffect, useMemo, useState } from "react";
import { Boxes, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const money = (v) => (v === null || v === undefined || v === "" ? "" : Number(v).toFixed(2));
const field = "border border-border rounded-sm px-2 py-2 text-sm w-full";

const StockRow = ({ p, onSaved }) => {
  const [stock, setStock] = useState(String(p.stock ?? 0));
  const [note, setNote] = useState(p.stock_note || "");
  const [price, setPrice] = useState(money(p.price));
  const [was, setWas] = useState(money(p.was_price));
  const [busy, setBusy] = useState(false);
  const qty = Math.max(0, parseInt(stock, 10) || 0);
  const stockDirty = qty !== p.stock || (qty === 0 && note.trim() !== (p.stock_note || ""));
  const priceDirty = money(price) !== money(p.price) || money(was) !== money(p.was_price);

  const save = async () => {
    const pr = parseFloat(price);
    const wp = was === "" ? null : parseFloat(was);
    if (priceDirty && (!(pr > 0) || (wp !== null && !(wp > pr)))) {
      toast.error("Price must be above £0, and any RRP must be higher than the selling price");
      return;
    }
    setBusy(true);
    try {
      let upd = {};
      if (priceDirty) upd = { ...upd, ...(await api.put(`/admin/products/${p.slug}/price`, { price: pr, was_price: wp })).data };
      if (stockDirty) upd = { ...upd, ...(await api.put(`/admin/products/${p.slug}/stock`, { stock: qty, stock_note: note })).data };
      if (upd.price !== undefined) { setPrice(money(upd.price)); setWas(money(upd.was_price)); }
      if (upd.stock !== undefined) { setStock(String(upd.stock)); setNote(upd.stock_note); }
      onSaved(upd);
      toast.success(`${p.name} updated`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save changes");
    } finally { setBusy(false); }
  };

  return (
    <div className="grid sm:grid-cols-[1fr_90px_90px_70px_1fr_auto] gap-3 items-center py-3 border-b border-border/60 last:border-0" data-testid={`stock-row-${p.slug}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium truncate" title={p.name}>{p.name}</p>
        <p className="text-xs text-muted-foreground font-mono">{p.sku}
          {p.stock === 0 && <span className="ml-2 font-sans text-destructive font-semibold">Out of stock</span>}
          {p.was_price && <span className="ml-2 font-sans text-accent font-semibold">Below RRP</span>}
        </p>
      </div>
      <input type="number" min="0.01" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} aria-label="Selling price"
        data-testid={`price-${p.slug}`} className={field} />
      <input type="number" min="0" step="0.01" value={was} onChange={(e) => setWas(e.target.value)} aria-label="RRP" placeholder="—"
        data-testid={`was-price-${p.slug}`} className={field} />
      <input type="number" min="0" value={stock} onChange={(e) => setStock(e.target.value)} aria-label="Stock quantity"
        data-testid={`stock-qty-${p.slug}`} className={field} />
      <input type="text" maxLength={80} value={qty > 0 ? "" : note} disabled={qty > 0} onChange={(e) => setNote(e.target.value)}
        placeholder={qty > 0 ? "Only used when out of stock" : "e.g. Due in w/c 2nd November"}
        data-testid={`stock-note-${p.slug}`} className={`${field} disabled:bg-muted/50`} />
      <button onClick={save} disabled={busy || !(stockDirty || priceDirty)} data-testid={`stock-save-${p.slug}`}
        className="bg-primary text-white text-sm font-semibold px-4 py-2 rounded-sm hover:bg-primary/90 disabled:opacity-40 transition-colors">
        {busy ? "Saving…" : "Save"}
      </button>
    </div>
  );
};

const HEAD = ["Product", "Price £", "RRP £", "Stock", "Arrival note (when 0)", ""];

export const AdminStockEditor = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [onlyOut, setOnlyOut] = useState(false);

  useEffect(() => { api.get("/admin/products/stock").then((r) => setItems(r.data)).catch(() => {}); }, []);

  const shown = useMemo(() => items.filter((p) =>
    (!onlyOut || p.stock === 0) && `${p.name} ${p.sku}`.toLowerCase().includes(q.toLowerCase())), [items, q, onlyOut]);

  const onSaved = (d) => setItems((prev) => prev.map((p) => (p.slug === d.slug ? { ...p, ...d } : p)));

  return (
    <div className="mt-12" data-testid="admin-stock-editor">
      <h2 className="font-display font-bold text-xl flex items-center gap-2 mb-1"><Boxes className="w-5 h-5 text-primary" /> Prices &amp; Stock</h2>
      <p className="text-sm text-muted-foreground mb-4">Add the manufacturer's RRP (higher than your price) to show "RRP £x · Save £y". Leave it empty to remove it. Set stock to 0 to add an arrival date. The date clears automatically once stock is above 0.</p>
      <div className="bg-card border border-border rounded-sm p-5">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or SKU" data-testid="stock-search"
              className="w-full border border-border rounded-sm pl-8 pr-3 py-2 text-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={onlyOut} onChange={(e) => setOnlyOut(e.target.checked)} className="accent-primary" data-testid="stock-only-out" />
            Out of stock only
          </label>
        </div>
        <div className="hidden sm:grid grid-cols-[1fr_90px_90px_70px_1fr_auto] gap-3 pt-2 pb-1 border-b border-border text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {HEAD.map((h, i) => <span key={h || i} className={i === 5 ? "w-[66px]" : ""}>{h}</span>)}
        </div>
        <div className="max-h-[560px] overflow-y-auto pr-1" data-testid="stock-list">
          {shown.map((p) => <StockRow key={p.slug} p={p} onSaved={onSaved} />)}
          {shown.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No products match.</p>}
        </div>
      </div>
    </div>
  );
};
